import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook para monitorar buscas em tempo real
 * @param {string} escolaId - ID da escola (para dashboard da escola)
 * @param {string} responsavelId - ID do responsável (para app dos pais)
 */
export const useRealtimeBuscas = (escolaId = null, responsavelId = null, enabled = true) => {
    const [buscas, setBuscas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }

        // Buscar dados iniciais
        fetchBuscas();

        // Configurar realtime subscription - Filtrado no servidor por ID
        const channel = supabase
            .channel(`buscas-${escolaId || responsavelId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'buscas',
                    filter: responsavelId
                        ? `responsavel_id=eq.${responsavelId}`
                        : (escolaId ? `escola_id=eq.${escolaId}` : undefined),
                },
                (payload) => {
                    handleRealtimeEvent(payload);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [escolaId, responsavelId, enabled]);

    const fetchBuscas = async () => {
        try {
            setLoading(true);
            let query = supabase
                .from('buscas')
                .select(`
          *,
          aluno:alunos(nome_completo, turma, portao_id, portao:portoes(nome)),
          responsavel:usuarios!responsavel_id(nome_completo, telefone)
        `)
                .order('criado_em', { ascending: false });

            if (escolaId) query = query.eq('escola_id', escolaId);
            if (responsavelId) query = query.eq('responsavel_id', responsavelId);

            const { data, error: fetchError } = await query;
            if (fetchError) throw fetchError;
            setBuscas(data || []);
        } catch (err) {
            console.error('Erro ao buscar buscas:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRealtimeEvent = async (payload) => {
        if (payload.eventType === 'DELETE') {
            setBuscas((prev) => prev.filter((busca) => busca.id !== payload.old.id));
            return;
        }

        if (payload.eventType === 'UPDATE') {
            // OTIMIZAÇÃO: Não faz novo SELECT. Atualiza apenas os campos que mudaram (status, timestamps)
            // Mantendo os dados de 'aluno' e 'responsavel' que já estavam no estado.
            setBuscas((prev) =>
                prev.map((busca) =>
                    busca.id === payload.new.id ? { ...busca, ...payload.new } : busca
                )
            );
            return;
        }

        if (payload.eventType === 'INSERT') {
            // Para a Escola, precisamos do refetch uma vez para pegar o nome do Aluno/Turma
            if (escolaId) {
                try {
                    const { data, error: fetchError } = await supabase
                        .from('buscas')
                        .select(`
                            *,
                            aluno:alunos(nome_completo, turma, portao_id, portao:portoes(nome)),
                            responsavel:usuarios!responsavel_id(nome_completo, telefone)
                        `)
                        .eq('id', payload.new.id)
                        .single();
                    if (!fetchError && data) {
                        setBuscas(prev => [data, ...prev]);
                    }
                } catch (e) {
                    setBuscas(prev => [payload.new, ...prev]);
                }
            } else {
                // Para o Pai, podemos inserir direto (ele já sabe quem é o filho)
                setBuscas(prev => [payload.new, ...prev]);
            }
        }
    };

    return { buscas, loading, error, refetch: fetchBuscas };
};

/**
 * Hook para atualizar status de busca
 */
export const useUpdateBusca = () => {
    const [updating, setUpdating] = useState(false);

    const updateStatus = async (buscaId, novoStatus, adminId = null) => {
        try {
            setUpdating(true);

            const updateData = {
                status: novoStatus,
                atualizado_em: new Date().toISOString(),
            };

            if (adminId) {
                updateData.atualizado_por_id = adminId;
            }

            // Adicionar timestamp específico do status
            switch (novoStatus) {
                case 'preparando':
                    updateData.preparando_em = new Date().toISOString();
                    break;
                case 'pronto':
                    updateData.pronto_em = new Date().toISOString();
                    break;
                case 'entregue':
                    updateData.entregue_em = new Date().toISOString();
                    break;
                case 'cancelado':
                    updateData.cancelado_em = new Date().toISOString();
                    break;
                default:
                    break;
            }

            const { data, error } = await supabase
                .from('buscas')
                .update(updateData)
                .eq('id', buscaId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (err) {
            console.error('Erro ao atualizar busca:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    };

    return { updateStatus, updating };
};
